import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Form, Input, Button, Card, Result, Progress, Alert, App } from 'antd';
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import passwordResetService from '../../services/passwordResetService';
import logger from '../../utils/logger';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const token = searchParams.get('token');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setTokenValid(false);
      setValidating(false);
      return;
    }

    try {
      setValidating(true);

      const response = await passwordResetService.validateResetToken(token);
      setTokenValid(response.valid);
    } catch (error) {
      logger.error('Token validation failed:', error);
      setTokenValid(false);
    } finally {
      setValidating(false);
    }
  };

  // Calculate password strength
  const calculatePasswordStrength = (password) => {
    if (!password) return 0;

    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (password.length >= 12) strength += 15;
    if (/[a-z]/.test(password)) strength += 15;
    if (/[A-Z]/.test(password)) strength += 15;
    if (/\d/.test(password)) strength += 15;
    if (/[^A-Za-z0-9]/.test(password)) strength += 15;

    return Math.min(strength, 100);
  };

  const getPasswordStrengthColor = (strength) => {
    if (strength < 30) return 'var(--rlc-state-error)';
    if (strength < 60) return 'var(--rlc-state-warning)';
    if (strength < 80) return 'var(--rlc-state-info)';
    return 'var(--rlc-state-success)';
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 30) return 'Weak';
    if (strength < 60) return 'Fair';
    if (strength < 80) return 'Good';
    return 'Strong';
  };

  const handleNewPasswordChange = (e) => {
    const password = e.target.value;
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      await passwordResetService.resetPassword(token, values.new_password);

      setResetSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { state: { message: 'Password reset successful. Please login with your new password.' } });
      }, 3000);
    } catch (error) {
      logger.error('Failed to reset password:', error);
      message.error(error.response?.data?.error || 'Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while validating token
  if (validating) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--rlc-surface-inverse-alt) 0%, var(--rlc-surface-inverse) 100%)',
        padding: '20px'
      }}>
        <Card style={{ maxWidth: 450, width: '100%', textAlign: 'center' }}>
          <div style={{ padding: '40px 0' }}>
            <div className="spinner" style={{ marginBottom: 16 }} />
            <p>Validating reset link...</p>
          </div>
        </Card>
      </div>
    );
  }

  // Show error if token is invalid
  if (!tokenValid) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--rlc-surface-inverse-alt) 0%, var(--rlc-surface-inverse) 100%)',
        padding: '20px'
      }}>
        <Card style={{ maxWidth: 500, width: '100%' }}>
          <Result
            status="error"
            icon={<CloseCircleOutlined style={{ color: 'var(--rlc-state-error)' }} />}
            title="Invalid or Expired Link"
            subTitle="This password reset link is invalid or has expired. Password reset links are valid for 24 hours."
            extra={[
              <Link to="/forgot-password" key="forgot">
                <Button type="primary">
                  Request New Link
                </Button>
              </Link>,
              <Link to="/login" key="login">
                <Button>
                  Back to Login
                </Button>
              </Link>
            ]}
          />
        </Card>
      </div>
    );
  }

  // Show success message
  if (resetSuccess) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, var(--rlc-surface-inverse-alt) 0%, var(--rlc-surface-inverse) 100%)',
        padding: '20px'
      }}>
        <Card style={{ maxWidth: 500, width: '100%' }}>
          <Result
            status="success"
            icon={<CheckCircleOutlined style={{ color: 'var(--rlc-state-success)' }} />}
            title="Password Reset Successful!"
            subTitle="Your password has been successfully reset. Redirecting to login..."
            extra={[
              <Link to="/login" key="login">
                <Button type="primary">
                  Go to Login
                </Button>
              </Link>
            ]}
          />
        </Card>
      </div>
    );
  }

  // Show reset password form
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, var(--rlc-surface-inverse-alt) 0%, var(--rlc-surface-inverse) 100%)',
      padding: '20px'
    }}>
      <Card style={{ maxWidth: 450, width: '100%' }}>
        <div className="auth-header">
          <h2>Reset Your Password</h2>
          <p style={{ color: 'var(--rlc-text-secondary)', marginTop: 8 }}>
            Enter your new password below
          </p>
        </div>

        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            label="New Password"
            name="new_password"
            rules={[
              { required: true, message: 'Please enter a new password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter new password (min 8 characters)"
              size="large"
              autoFocus
              onChange={handleNewPasswordChange}
            />
          </Form.Item>

          {/* Password strength indicator */}
          {form.getFieldValue('new_password') && (
            <div style={{ marginBottom: 16, marginTop: -8 }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 4,
                fontSize: '12px'
              }}>
                <span style={{ color: 'var(--rlc-text-secondary)' }}>Password Strength:</span>
                <span style={{
                  fontWeight: 600,
                  color: getPasswordStrengthColor(passwordStrength)
                }}>
                  {getPasswordStrengthText(passwordStrength)}
                </span>
              </div>
              <Progress
                percent={passwordStrength}
                strokeColor={getPasswordStrengthColor(passwordStrength)}
                showInfo={false}
                size="small"
              />
            </div>
          )}

          <Form.Item
            label="Confirm Password"
            name="confirm_password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm new password"
              size="large"
            />
          </Form.Item>

          <Alert
            message="Password Requirements"
            description={
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: '12px' }}>
                <li>At least 8 characters long</li>
                <li>Mix of uppercase and lowercase letters (recommended)</li>
                <li>Include numbers (recommended)</li>
                <li>Include special characters (recommended)</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
            >
              Reset Password
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Link to="/login">
              <Button type="link">
                Back to Login
              </Button>
            </Link>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default ResetPassword;
